import React, { useCallback, useMemo, useState } from "react";
import { Download, Ellipsis, LucideIcon, Play, RefreshCw, Trash2, Video } from "lucide-react";
import { ContentPage, Media, SuperPage } from "@/services/bedrock";
import { useSocialCoachSession } from "@/utils";
import { Button, confirmDialog, Dialog, DropdownMenu, Separator } from "@/components/ui";
import { MediaPlayer } from "@/components";
import { VideoPageSourceModal } from "../../VideoPageSourceModal";

type VideoActionMenuItemProps = {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  withSeparator?: boolean;
  destructive?: boolean;
};

type VideoActionsMenuProps = {
  actions: VideoActionMenuItemProps[];
};

const VideoActionsMenu = ({ actions }: VideoActionsMenuProps) => (
  <DropdownMenu>
    <DropdownMenu.Trigger
      render={props => (
        <Button {...props} variant="ghost" size="icon">
          <span className="sr-only">Open menu</span>
          <Ellipsis className="size-4 color-foreground" />
        </Button>
      )}
    />
    <DropdownMenu.Content align="end" className="w-auto min-w-max">
      <DropdownMenu.Group>
        {actions.map(action => (
          <React.Fragment key={action.label}>
            {action.withSeparator && <DropdownMenu.Separator />}
            <DropdownMenu.Item
              onClick={action.onClick}
              variant={action.destructive ? "destructive" : "default"}
              className="whitespace-nowrap flex items-center gap-2 p-2"
            >
              <action.icon />
              {action.label}
            </DropdownMenu.Item>
          </React.Fragment>
        ))}
      </DropdownMenu.Group>
    </DropdownMenu.Content>
  </DropdownMenu>
);

const VideoTile = ({
  video,
  thumbnailUrl,
  onClick,
}: {
  video: Media;
  thumbnailUrl?: string | null;
  onClick: () => void;
}) => {
  return (
    <div
      className="relative w-full h-full cursor-pointer group flex items-center justify-center bg-slate-900 overflow-hidden"
      onClick={onClick}
    >
      {thumbnailUrl ? (
        <img src={thumbnailUrl} alt="Video thumbnail" className="w-full h-full object-contain" />
      ) : (
        <video src={video.uri} className="w-full h-full object-contain" />
      )}
      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
        <div className="flex items-center justify-center rounded-full bg-white/90 group-hover:bg-white group-hover:scale-110 text-slate-900 size-12 shadow-md transition-all">
          <Play className="size-6 fill-slate-900 ml-0.5" />
        </div>
      </div>
    </div>
  );
};

export type EditVideoPageVideoSectionProps = {
  videoPage: ContentPage;
  superPage: SuperPage;
  onUpdateMedia: (media: Media[]) => void;
};

const EditVideoPageVideoSection = ({ videoPage, superPage, onUpdateMedia }: EditVideoPageVideoSectionProps) => {
  const { playerId } = useSocialCoachSession();
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [selectedVideoForPlayer, setSelectedVideoForPlayer] = useState<Media | null>(null);

  const currentVideo = useMemo(() => videoPage.originalMedia?.[0], [videoPage.originalMedia]);
  const defaultVideo = useMemo(
    () => superPage.templateContentPage?.originalMedia?.[0],
    [superPage.templateContentPage]
  );

  const confirmRemoveVideo = useCallback(async () => {
    if (!videoPage.id || !defaultVideo) return;

    const result = await confirmDialog({
      title: "Are you sure?",
      description: "By removing this video, you will revert to the default video for this assignment.",
      confirmLabel: "Remove",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (result) {
      onUpdateMedia([]);
    }
  }, [videoPage.id, onUpdateMedia, defaultVideo]);

  const replaceVideo = useCallback(
    (media: Media) => {
      setShowSourceModal(false);
      onUpdateMedia([media]);
    },
    [onUpdateMedia]
  );

  const downloadVideo = (media: Media) => {
    const filename = `video-${videoPage.slug}.${media.extension}`;
    fetch(media.uri)
      .then(response => response.blob())
      .then(blob => {
        const urlObject = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = urlObject;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(urlObject);
      });
  };

  const currentVideoActions = useMemo<VideoActionMenuItemProps[]>(() => {
    const items = [];
    items.push({ icon: Download, label: "Download", onClick: () => currentVideo && downloadVideo(currentVideo) });
    if (defaultVideo) {
      items.push({
        icon: Trash2,
        label: "Remove",
        onClick: confirmRemoveVideo,
        withSeparator: true,
        destructive: true,
      });
    }
    return items;
  }, [videoPage, currentVideo, playerId, confirmRemoveVideo, defaultVideo]);

  return (
    <div>
      <VideoPageSourceModal
        open={showSourceModal}
        title="Edit Video Page"
        hideScriptSelection
        videoScript={superPage.videoScript}
        onOpenChange={setShowSourceModal}
        onMediaSelected={replaceVideo}
      />
      <div className="flex gap-4">
        <div className="flex flex-col gap-2 max-w-1/2 w-1/2">
          <div className="flex justify-between items-center min-h-8">
            <div className="font-semibold text-sm text-foreground">Uploaded Video</div>
            {!!currentVideo && <VideoActionsMenu actions={currentVideoActions} />}
          </div>
          <div className="border border-border rounded-md overflow-hidden bg-gray-100 h-44">
            {currentVideo ? (
              <VideoTile
                video={currentVideo}
                thumbnailUrl={videoPage.thumbnailUrl}
                onClick={() => {
                  setSelectedVideoForPlayer(currentVideo);
                  setShowPlayerModal(true);
                }}
              />
            ) : (
              <div className="p-4 flex flex-col items-center justify-center h-full">
                <p className="mt-0 text-xs text-center">
                  No video has been uploaded for this assignment. The default video will be used unless you upload
                  another using the button below.
                </p>
                <Button onClick={() => setShowSourceModal(true)}>Add a Video</Button>
              </div>
            )}
          </div>
          {!!currentVideo && (
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs gap-1.5 border-indigo-200 text-indigo-600 font-semibold hover:border-indigo-300 hover:bg-indigo-50/10 transition-colors"
              onClick={() => setShowSourceModal(true)}
            >
              <RefreshCw className="size-3.5" />
              Replace Video
            </Button>
          )}
        </div>

        {!!defaultVideo && (
          <>
            <Separator orientation="vertical" />
            <div className="flex flex-col gap-2 max-w-1/2 w-1/2">
              <div className="flex justify-between items-center min-h-8">
                <div className="font-semibold text-sm text-foreground">Default Video</div>
              </div>
              <div className="border border-border rounded-md overflow-hidden bg-gray-100 h-44">
                <VideoTile
                  video={defaultVideo}
                  thumbnailUrl={superPage.templateContentPage?.thumbnailUrl}
                  onClick={() => {
                    setSelectedVideoForPlayer(defaultVideo);
                    setShowPlayerModal(true);
                  }}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs gap-1.5"
                onClick={() => downloadVideo(defaultVideo)}
              >
                <Download className="size-3.5" />
                Download Video
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Media Player Modal */}
      {showPlayerModal && selectedVideoForPlayer && (
        <Dialog
          open={showPlayerModal}
          onOpenChange={isOpen => {
            if (!isOpen) {
              setShowPlayerModal(false);
              setSelectedVideoForPlayer(null);
            }
          }}
          size="lg"
          showCloseButton={true}
          className="max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden"
        >
          <Dialog.Header>
            <Dialog.Title className="flex items-center gap-1.5 text-slate-800 text-base font-bold">
              <Video className="size-5 text-indigo-500" />
              <span>Watch Video</span>
            </Dialog.Title>
          </Dialog.Header>

          <Dialog.Content className="p-0 bg-black flex-1 flex items-center justify-center overflow-hidden rounded-b-xl">
            <div className="w-full h-full flex items-center justify-center bg-black overflow-hidden [&_.mediaContainer]:w-full [&_.mediaContainer]:h-full [&_.mediaContainer]:flex [&_.mediaContainer]:items-center [&_.mediaContainer]:justify-center [&_video]:max-h-[calc(100vh-10rem)] [&_video]:max-w-full [&_video]:object-contain [&_video]:min-h-0">
              <MediaPlayer media={selectedVideoForPlayer} autoPlay={true} />
            </div>
          </Dialog.Content>
        </Dialog>
      )}
    </div>
  );
};

export default EditVideoPageVideoSection;
